import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Folder, LayoutGrid } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/books/ui";

/** Every report SyncBooks can build from Tally data, in Zoho's categories. */
export const REPORTS = [
  { key: "profit-and-loss", name: "Profit and Loss", category: "Business Overview" },
  { key: "horizontal-profit-and-loss", name: "Horizontal Profit and Loss", category: "Business Overview" },
  { key: "balance-sheet", name: "Balance Sheet", category: "Business Overview" },
  { key: "horizontal-balance-sheet", name: "Horizontal Balance Sheet", category: "Business Overview" },
  { key: "sales-by-customer", name: "Sales by Customer", category: "Sales" },
  { key: "sales-by-item", name: "Sales by Item", category: "Sales" },
  { key: "ar-aging-summary", name: "AR Aging Summary", category: "Receivables" },
  { key: "ar-aging-details", name: "AR Aging Details", category: "Receivables" },
  { key: "customer-balance-summary", name: "Customer Balance Summary", category: "Receivables" },
  { key: "ap-aging-summary", name: "AP Aging Summary", category: "Payables" },
  { key: "ap-aging-details", name: "AP Aging Details", category: "Payables" },
  { key: "vendor-balance-summary", name: "Vendor Balance Summary", category: "Payables" },
  { key: "purchases-by-vendor", name: "Purchases by Vendor", category: "Purchases and Expenses" },
  { key: "purchases-by-item", name: "Purchases by Item", category: "Purchases and Expenses" },
  { key: "stock-summary", name: "Stock Summary", category: "Inventory" },
  { key: "trial-balance", name: "Trial Balance", category: "Accountant" },
  { key: "general-ledger", name: "General Ledger", category: "Accountant" },
  { key: "account-transactions", name: "Account Transactions", category: "Accountant" },
  { key: "day-book", name: "Day Book", category: "Accountant" },
];

const CATEGORIES = [...new Set(REPORTS.map((r) => r.category))];

export default function Reports() {
  const [category, setCategory] = useState(null);
  const [q, setQ] = useState("");
  const rows = useMemo(() => REPORTS.filter((r) => (!category || r.category === category)
    && (!q.trim() || r.name.toLowerCase().includes(q.trim().toLowerCase()))), [category, q]);
  return (
    <div>
      <PageHeader title="Reports Center">
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reports" className="w-72 pl-8" data-testid="report-search" />
        </div>
      </PageHeader>
      <div className="grid gap-5 lg:grid-cols-[240px_1fr]">
        <nav className="h-fit space-y-0.5 rounded-lg border border-slate-200 bg-white p-2">
          <button onClick={() => setCategory(null)}
            className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm ${!category ? "bg-blue-50 font-medium text-blue-700" : "text-slate-700 hover:bg-slate-50"}`}>
            <LayoutGrid size={15} /> All reports
          </button>
          <div className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Report category</div>
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCategory(c)}
              className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm ${category === c ? "bg-blue-50 font-medium text-blue-700" : "text-slate-700 hover:bg-slate-50"}`}>
              <Folder size={15} className="text-slate-400" /> {c}
            </button>
          ))}
        </nav>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-5 py-3 text-[15px] font-semibold">
            {category || "All reports"} <span className="ml-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">{rows.length}</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-5 py-2.5 text-left">Report name</th>
                <th className="px-5 py-2.5 text-left">Report category</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-slate-100 last:border-0 hover:bg-blue-50/40">
                  <td className="px-5 py-3"><Link to={`/reports/${r.key}`} className="text-blue-700 hover:underline">{r.name}</Link></td>
                  <td className="px-5 py-3 text-slate-600">{r.category}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
